import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import uniq from 'lodash-es/uniq';
import { ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSubscription } from '../../hooks/useSubscription';

import { usePopupsContext } from '../../contexts/PopupsContext';
import { useSetting, useSubscriptions } from '../../contexts/SiteContext';
import FormButton from '../buttons/FormButton';
import SsoButton from '../buttons/SsoButton';
import SubscriptionHorizontalCard from '../cards/SubscriptionHorizontalCard';
import ContactField from '../fields/ContactField';
import UserField from '../fields/UserField';
import LoadingIcon from '../icons/LoadingIcon';
import FormStatus from '../partials/FormStatus';
import EditContactModal from '../popups/EditContactModal';
import FormControl from './FormControl';

import styles from '../../styles/forms/subscription-form.module.css';

export interface SubscriptionFormProps {
    subscription?: string | null;
    subscriptions?: string[] | null;
    source?: string | null;
    placeholder?: string | null;
    label?: ReactNode;
    via?: string | string[] | null;
    full?: boolean;
    buttonOnly?: boolean;
    subscribeButtonLabel?: ReactNode;
    compact?: boolean;
    alwaysHorizontal?: boolean;
    alwaysVertical?: boolean;
    ssoSize?: string | null;
    ssoTheme?: string | null;
    withoutClose?: boolean;
    withoutCancel?: boolean;
    withoutButtons?: boolean;
    withoutSubscriptions?: boolean;
    withoutOtherSubscriptions?: boolean;
    withoutLogout?: boolean;
    withoutMessages?: boolean;
    withoutSso?: boolean;
    withoutLogin?: boolean;
    friend?: boolean;
    ssoOnly?: boolean;
    withoutEditContact?: boolean;
    fieldRef?: Ref<HTMLDivElement> | null;
    inputRef?: Ref<HTMLInputElement> | null;
    onFocus?: (() => void) | null;
    onBlur?: (() => void) | null;
    onComplete?: ((response: unknown, method: string) => void) | null;
    onCancel?: (() => void) | null;
    onFieldPointerUp?: (() => void) | null;
    onChange?: ((value: { email?: string; phone?: string; field?: string } | null) => void) | null;
    onClickSso?: ((provider: string) => void) | null;
    className?: string | null;
    fieldsClassName?: string | null;
    buttonsClassName?: string | null;
    subscriptionsClassName?: string | null;
    ssoClassName?: string | null;
}

function SubscriptionForm({
    subscription: subscriptionHandle = null,
    subscriptions: subscriptionHandles = null,
    source = null,
    placeholder = null,
    label = null,
    full = false,
    via = null,
    buttonOnly = false,
    subscribeButtonLabel = null,
    compact = false,
    alwaysHorizontal = false,
    alwaysVertical = false,
    ssoSize = null,
    ssoTheme = null,
    withoutClose = false,
    withoutCancel = false,
    withoutButtons = false,
    withoutSubscriptions = false,
    withoutOtherSubscriptions = false,
    withoutLogout = false,
    withoutMessages = false,
    withoutEditContact = false,
    withoutSso = false,
    withoutLogin = false,
    friend = false,
    ssoOnly = false,
    fieldRef = null,
    inputRef = null,
    onFocus = null,
    onBlur = null,
    onComplete = null,
    onCancel = null,
    onFieldPointerUp = null,
    onChange = null,
    onClickSso: customOnClickSso = null,
    className = null,
    fieldsClassName = null,
    buttonsClassName = null,
    subscriptionsClassName = null,
    ssoClassName = null,
}: SubscriptionFormProps) {
    const ssoButtons = useSetting('ssoButtons', ['google', 'facebook']);
    const allSubscriptions = useSubscriptions();
    const {
        subscribe,
        subscribed,
        subscription,
        editContactNeeded,
        closeEditContact,
        onEditContactComplete,
        sourceWithPageKey,
    } = useSubscription({
        subscription: subscriptionHandle,
        source,
        friend,
        withoutLogin,
        withoutMessages,
        withoutEditContact,
    });
    const { addMessage } = usePopupsContext();
    const { via: subscriptionVia = null } = subscription || {};
    const finalVia = via || subscriptionVia;
    const onlyVia = finalVia !== null && finalVia.length === 1 ? finalVia[0] : null;
    const formFields = ['field', 'phone', 'email'];
    const tracking = useTracking();

    const [focused, setFocused] = useState(false);
    const [fieldType, setFieldType] = useState(onlyVia === 'sms' ? 'tel' : 'email');
    const onFieldTypeChange = (newType) => setFieldType(newType);

    const onFieldFocus = () => {
        setFocused(true);
    };

    const allHandles = uniq(
        [...(subscriptionHandles || []), subscriptionHandle].filter((it) => it !== null),
    );

    const [selectedSubscriptions, setSelectedSubscriptions] = useState(
        allSubscriptions
            .filter(
                ({ handle, group }) =>
                    allHandles.indexOf(handle) !== -1 || allHandles.indexOf(group) !== -1,
            )
            .map(({ handle }) => handle),
    );

    const user = useUser();

    const contactFieldName = fieldType === 'tel' ? 'phone' : 'email';
    const postSendToken = (formAction, { field }) =>
        subscribe(
            selectedSubscriptions.length > 1 || subscriptionHandles !== null
                ? {
                      subscriptions: selectedSubscriptions,
                      [contactFieldName]: field,
                  }
                : {
                      [contactFieldName]: field,
                  },
        );
    const setValueRef = useRef(null);
    const onFormComplete = (response) => {
        if (setValueRef.current !== null) {
            setValueRef.current(null);
        }
        if (onComplete !== null) {
            onComplete(response, fieldType === 'tel' ? 'phone' : 'email');
        }
    };

    const { fields, onSubmit, status, value, setValue } = useForm({
        fields: formFields,
        postForm: postSendToken,
        onComplete: onFormComplete,
    });

    useImperativeHandle(setValueRef, () => setValue, [setValue]);

    useEffect(() => {
        if (onChange !== null) {
            onChange(value);
        }
    }, [value]);

    const { errors } = fields[fieldType === 'tel' ? 'phone' : 'email'] || {};

    const availableSubscriptions = allSubscriptions
        .filter(
            ({ handle, type }) =>
                (type !== 'notification' && type !== 'external' && !withoutOtherSubscriptions) ||
                allHandles.indexOf(handle) !== -1,
        )
        .sort(({ handle: aHandle }, { handle: bHandle }) => {
            if (allHandles.indexOf(aHandle) !== -1) {
                return -1;
            }
            if (allHandles.indexOf(bHandle) !== -1) {
                return 1;
            }
            return 0;
        });

    const onSsoComplete = (provider, response) => {
        tracking.trackEvent('Auth', 'login', provider);

        if (!withoutMessages) {
            addMessage('subscribe', {
                subscription: allSubscriptions.find(
                    ({ handle }) => allHandles.indexOf(handle) !== -1,
                ),
            });
        }

        const { newSubscriptions = allHandles } = response || {};

        const subscriptionHandles = newSubscriptions || [];
        if (subscriptionHandles.length > 0) {
            tracking.trackSubscribe(subscriptionHandles, sourceWithPageKey);
        }

        if (onComplete !== null) {
            onComplete(response, provider);
        }
    };

    const onClickSso = (provider: string) => {
        if (customOnClickSso !== null) {
            customOnClickSso(provider);
        }
        tracking.trackEvent('Auth', 'click_sso', provider);
    };

    const onSubscriptionChange = ({ handle }, checked) => {
        setSelectedSubscriptions(
            checked
                ? [...selectedSubscriptions, handle]
                : selectedSubscriptions.filter((s) => s !== handle),
        );
    };

    const onClickCancel = () => {
        setValue(null);
        if (onCancel !== null) {
            onCancel();
        }
    };

    const hasUser = user !== null && !withoutLogin && !friend;

    return (
        <form
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                    [styles.subscribed]: subscribed,
                    [styles.loading]: status === 'loading',
                    [styles.full]: full,
                    [styles.buttonOnly]: buttonOnly && !focused,
                    [styles.ssoOnly]: ssoOnly,
                    [styles.hasUser]: hasUser,
                    [styles.alwaysHorizontal]: alwaysHorizontal,
                    [styles.alwaysVertical]: alwaysVertical,
                    [className]: className !== null,
                },
            ])}
            data-nosnippet
            onSubmit={onSubmit}
        >
            {!ssoOnly ? (
                <FormControl
                    className={classNames([styles.fields, fieldsClassName])}
                    label={label}
                    {...fields[fieldType === 'tel' ? 'phone' : 'email']}
                >
                    <div
                        className={styles.fieldGroup}
                        ref={fieldRef}
                        data-theme={focused ? 'normal' : null}
                    >
                        {hasUser ? (
                            <UserField
                                className={styles.field}
                                inputClassName={styles.input}
                                withBorder
                                withoutLogout={withoutLogout}
                            />
                        ) : (
                            <ContactField
                                {...fields.field}
                                withBorder
                                type={onlyVia === 'sms' ? 'tel' : onlyVia}
                                ref={inputRef}
                                errors={errors}
                                className={styles.field}
                                inputClassName={styles.input}
                                placeholder={placeholder}
                                onTypeChange={onFieldTypeChange}
                                onFocus={onFieldFocus}
                                onPointerUp={onFieldPointerUp}
                                onBlur={onBlur}
                            />
                        )}
                        <LoadingIcon className={styles.loadingIcon} />
                        <FormButton
                            type="submit"
                            compact={compact && !focused}
                            disabled={status === 'loading'}
                            className={styles.primaryButton}
                        >
                            {subscribeButtonLabel || (
                                <FormattedMessage
                                    defaultMessage="S'abonner"
                                    description="Button label"
                                />
                            )}
                        </FormButton>
                    </div>
                </FormControl>
            ) : null}

            {!withoutSso && user === null && !withoutLogin && !friend ? (
                <div className={classNames([styles.sso, ssoClassName])}>
                    {!ssoOnly ? (
                        <div className={styles.or}>
                            <FormattedMessage defaultMessage="ou" description="Spacer label" />
                        </div>
                    ) : null}
                    {ssoButtons.map((provider) => (
                        <SsoButton
                            key={`sso-${provider}`}
                            provider={provider}
                            source={sourceWithPageKey}
                            size={ssoSize || 'medium'}
                            theme={ssoTheme || 'light'}
                            subscription={allHandles}
                            className={styles.button}
                            onComplete={(newUser) => onSsoComplete(provider, newUser)}
                            onClick={(e) => onClickSso(provider, e)}
                        />
                    ))}
                </div>
            ) : null}

            {!withoutSubscriptions ? (
                <div className={classNames([styles.subscriptions, subscriptionsClassName])}>
                    {availableSubscriptions.map((subscriptionItem) => (
                        <SubscriptionHorizontalCard
                            key={`subscription-${subscriptionItem.handle}`}
                            {...subscriptionItem}
                            theme="box"
                            // thumbnailWidth={50}
                            source={source !== null ? `${source}:suggestion` : 'suggestion'}
                            checked={selectedSubscriptions.indexOf(subscriptionItem.handle) !== -1}
                            withCheckbox={!subscribed}
                            withToggle={subscribed}
                            className={styles.subscription}
                            onChange={(checked) => onSubscriptionChange(subscriptionItem, checked)}
                        />
                    ))}
                </div>
            ) : null}
            {!withoutButtons ? (
                <div className={classNames([styles.buttons, buttonsClassName])}>
                    {!subscribed && !withoutCancel ? (
                        <FormButton
                            transparent
                            className={styles.secondaryButton}
                            onClick={onClickCancel}
                        >
                            <FormattedMessage defaultMessage="Annuler" description="Button label" />
                        </FormButton>
                    ) : null}
                    <FormStatus
                        status={status}
                        successMessage="Merci!"
                        iconRight
                        className={styles.status}
                    />
                    {!subscribed ? (
                        <FormButton
                            type="submit"
                            className={styles.primaryButton}
                            disabled={status === 'loading'}
                        >
                            {subscribeButtonLabel || (
                                <FormattedMessage
                                    defaultMessage="S'abonner"
                                    description="Button label"
                                />
                            )}
                        </FormButton>
                    ) : null}

                    {subscribed && !withoutClose ? (
                        <FormButton
                            type="button"
                            className={styles.primaryButton}
                            onClick={onClickCancel}
                        >
                            <FormattedMessage defaultMessage="Fermer" description="Button label" />
                        </FormButton>
                    ) : null}
                </div>
            ) : null}

            {editContactNeeded !== null ? (
                <EditContactModal
                    type={editContactNeeded}
                    withoutUpdate={user === null}
                    title={
                        editContactNeeded === 'phone' ? (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un numéro de téléphone"
                                description="Modal title"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un courriel"
                                description="Modal title"
                            />
                        )
                    }
                    onComplete={onEditContactComplete}
                    onClosed={closeEditContact}
                />
            ) : null}
        </form>
    );
}

export default SubscriptionForm;
